import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import axios from "../../../../api/axios";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../../components/ui/detailView";
import {
  buildClientInvoiceSettingsEndpoint,
  CLIENT_INVOICE_SETTINGS_UPDATED_EVENT,
  INVOICE_GROUPING_LABEL_KEYS,
  INVOICE_PAYMENT_METHOD_LABEL_KEYS,
  RECEIVER_ROLE_LABEL_KEYS,
  VAT_RATE_LABEL_KEYS,
} from "../../../../consts";
import { useHasPermission } from "../../../../contexts/auth";
import { useNumerationSchemaNames } from "../../../../hooks/useTenantSettings";
import { ClientInvoiceSettings as ClientInvoiceSettingsData } from "../../../../types/invoices";

const EMPTY = "—";

/**
 * A client's own value, or - when it has none - what the workspace default
 * currently resolves to, set apart so it is clear the client inherits it.
 */
const DefaultedValue = ({
  own,
  workspace,
}: {
  own: string | null;
  workspace: string | null;
}) => {
  const { t } = useTranslation();

  if (own) {
    return <DetailValue>{own}</DetailValue>;
  }

  return (
    <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
      {workspace
        ? t("Workspace default ({{value}})", { value: workspace })
        : t("Workspace default")}
    </Typography>
  );
};

export const ClientInvoiceSettings = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canView = useHasPermission("invoice.view_invoice");
  const schemaNames = useNumerationSchemaNames();
  const [settings, setSettings] = useState<ClientInvoiceSettingsData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setOverride(location.pathname, t("Invoice defaults"));
    return () => {
      clearOverride(location.pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const fetchSettings = useCallback(async () => {
    if (!id) return;

    try {
      const { data } = await axios.get<ClientInvoiceSettingsData>(
        buildClientInvoiceSettingsEndpoint(id)
      );
      setSettings(data);
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSettings();
    window.addEventListener(CLIENT_INVOICE_SETTINGS_UPDATED_EVENT, fetchSettings);
    return () =>
      window.removeEventListener(
        CLIENT_INVOICE_SETTINGS_UPDATED_EVENT,
        fetchSettings
      );
  }, [fetchSettings]);

  if (!canView) {
    return null;
  }

  if (loading) {
    return <DetailPage loading />;
  }

  if (!settings) {
    return <Typography>{t("There was an error loading the data.")}</Typography>;
  }

  const workspace = settings.workspace_defaults;
  const vatLabel = (value?: string | null) =>
    value ? t(VAT_RATE_LABEL_KEYS[value] ?? value) : null;
  const paymentLabel = (value?: string | null) =>
    value ? t(INVOICE_PAYMENT_METHOD_LABEL_KEYS[value] ?? value) : null;
  // A bare number under a "days" label: Polish day counts do not take one
  // plural form, and zero ("due on the issue date") must still show.
  const termLabel = (value?: number | null) =>
    value !== null && value !== undefined ? String(value) : null;
  const groupingLabel = (value?: string | null) =>
    value ? t(INVOICE_GROUPING_LABEL_KEYS[value] ?? value) : null;
  const schemaLabel = (value?: number | null) =>
    value !== null && value !== undefined
      ? schemaNames[String(value)] ?? null
      : null;

  return (
    <DetailPage>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Defaults new invoices for this client start from. Anything left on the workspace default follows the workspace invoicing settings."
        )}
      </Typography>

      <DetailSection title={t("Invoice defaults")}>
        <DetailRow
          label={t("VAT rate")}
          value={
            <DefaultedValue
              own={vatLabel(settings.vat_rate)}
              workspace={vatLabel(workspace?.vat_rate)}
            />
          }
        />
        <DetailRow
          label={t("Payment method")}
          value={
            <DefaultedValue
              own={paymentLabel(settings.payment_method)}
              workspace={paymentLabel(workspace?.payment_method)}
            />
          }
        />
        <DetailRow
          label={t("Payment term days")}
          value={
            <DefaultedValue
              own={termLabel(settings.payment_term_days)}
              workspace={termLabel(workspace?.payment_term_days)}
            />
          }
        />
        <DetailRow
          label={t("Numeration schema")}
          value={
            <DefaultedValue
              own={schemaLabel(settings.numeration_schema_id)}
              workspace={schemaLabel(workspace?.numeration_schema_id)}
            />
          }
        />
      </DetailSection>

      <DetailSection title={t("Automated invoicing")}>
        <DetailRow
          label={t("Invoice grouping")}
          value={
            <DefaultedValue
              own={groupingLabel(settings.invoice_grouping)}
              workspace={groupingLabel(workspace?.invoice_grouping)}
            />
          }
        />
      </DetailSection>

      <DetailSection title={t("Receiver")}>
        {settings.receiver_id ? (
          <>
            <DetailRow
              label={t("Name")}
              value={<DetailValue>{settings.receiver_name}</DetailValue>}
            />
            <DetailRow
              label={t("NIP")}
              value={<DetailValue>{settings.receiver_nip || EMPTY}</DetailValue>}
            />
            <DetailRow
              label={t("Receiver role")}
              value={
                <DetailValue>
                  {t(
                    RECEIVER_ROLE_LABEL_KEYS[settings.receiver_role] ??
                      settings.receiver_role
                  )}
                </DetailValue>
              }
            />
          </>
        ) : (
          <DetailRow
            label={t("Receiver")}
            value={
              <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
                {t("No receiver - invoices name only the buyer")}
              </Typography>
            }
          />
        )}
      </DetailSection>
    </DetailPage>
  );
};
